import { Typography, Rate, Avatar } from 'antd';
import { LeftOutlined, RightOutlined, EnvironmentOutlined, MailOutlined, PhoneOutlined, UserOutlined } from "@ant-design/icons";
import { HiOutlineLightBulb } from 'react-icons/hi';
import { PiLeaf } from 'react-icons/pi';
import { FiUsers } from 'react-icons/fi';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { ProductCard } from '@components/molecules/ProductCard/ProductCard';
import { ContactExpertsModal } from '@components/molecules';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTestimonials } from '@hooks';

// Importe os estilos do Swiper
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';

const { Title, Paragraph } = Typography;

// 1. DEFININDO O TIPO PARA UM PRODUTO
interface Product {
  id: number;
  name: string;
  description: string;
  imageUrl: string;
}

const products: Product[] = [
  { id: 1, name: 'Fabrication', description: 'Custom truck beds, bumpers, toolboxes, welded mounts & frames', imageUrl: '/assets/images/FabricationExample.png' },
  { id: 2, name: 'Ladder Chain', description: 'Heavy Duty Square link chains', imageUrl: '/assets/images/LadderChainHook.png' },
  { id: 3, name: 'Revo Hook', description: 'Our patented Revo Hook is the better swivel hook for your tire chains. It locks in like a puzzle and stays tight in the roughest winter conditions', imageUrl: '/assets/images/RevoHook.png' },
  { id: 4, name: 'Basket Chain', description: 'Tough, square-link basket chains with reinforced wear bars for maximum longevity.', imageUrl: '/assets/products/BasketChain.png' },
];

const loopedProducts = [...products, ...products];

export const LandingPage = () => {
  const navigate = useNavigate();
  const [imageOpacity, setImageOpacity] = useState(1); // 0 = ladder visível, 1 = wireframe da engenharia visível (começar em 0.6 = mais engenharia)
  const [isContactModalVisible, setIsContactModalVisible] = useState(false);
  const { getActiveTestimonials } = useTestimonials();
  
  // Fetch active testimonials with a limit of 6
  const { data: testimonialsData } = getActiveTestimonials({ limit: 6 });
  const testimonials = testimonialsData?.data || [];

  useEffect(() => {
    const handleScroll = () => {
      // Calcula a porcentagem de scroll da página
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.min(scrollTop / docHeight, 1);
      
      // Mapeia a porcentagem de scroll para revelar o wireframe
      // Revelação muito rápida da engenharia - começa imediatamente no scroll
      const maxScrollForTransition = 0.08; // Transição completa nos primeiros 8% do scroll (muito mais rápido)
      const transitionPercent = Math.min(scrollPercent / maxScrollForTransition, 1);
      
      // Aplica uma curva de easing suave para transição natural
      const easedTransition = transitionPercent * transitionPercent * (3 - 2 * transitionPercent);
      
      // Atualiza a opacidade: 0 = ladder visível, 1 = wireframe da engenharia visível
      setImageOpacity(easedTransition);
    };

    // Atualiza na primeira renderização
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      {/* 1 - Hero Section */}
      <section 
      className="relative w-full h-screen min-h-[700px] flex flex-col overflow-hidden">
      {/* === Contêiner para Vídeo e Imagens de Fundo === */}
      <div className="absolute top-0 left-0 w-full h-full z-0">
        {/* Cálculo unificado da posição diagonal - uma única barra que divide tudo */}
        {/* A diagonal vai de (topX, 0) até (topX - 10, 100%) para criar a inclinação */}
        {(() => {
          // Posição inicial: 75% do topo, 65% da base (diagonal inclinada)
          // Posição final: 45% do topo, 35% da base
          const topPos = 75 - (imageOpacity * 30);
          const bottomPos = 65 - (imageOpacity * 30);
          
          return (
            <>
              {/* Vídeo de Fundo - Lado esquerdo da diagonal */}
              <div className="absolute top-0 left-0 w-full h-full z-1">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute top-0 left-0 w-full h-full object-cover"
                  style={{
                    clipPath: `polygon(0 0, ${topPos + 2}% 0, ${bottomPos + 2}% 100%, 0 100%)`
                  }}
                >
                  <source src="/assets/videos/HomepageBackground.mkv" type="video/webm" />
                  Your browser does not support this video.
                </video>
              </div>

              {/* Fundo escuro - Lado direito da diagonal (sobrepõe levemente para eliminar gap) */}
              <div 
                className="absolute top-0 left-0 w-full h-full bg-gray-800 z-5"
                style={{
                  clipPath: `polygon(${topPos - 2}% 0, 100% 0, 100% 100%, ${bottomPos - 2}% 100%)`
                }}
              />
              
              {/* Container para as imagens do pneu */}
              <div className="absolute inset-0 flex items-center justify-end pr-4 md:pr-8 lg:pr-12 z-50">
                <div className="relative w-80 h-80 md:w-[28rem] md:h-[28rem] lg:w-[32rem] lg:h-[32rem] xl:w-[36rem] xl:h-[36rem]">
                  {/* Imagem base - Ladder Chain Hook (lado esquerdo da diagonal) */}
                  <img
                    src="/assets/images/LadderChainHook.png"
                    alt="Ladder Chain Hook - Gancho para correntes de pneu"
                    className="absolute inset-0 w-full h-full object-contain"
                    style={{
                      clipPath: `polygon(0 0, ${topPos + 2}% 0, ${bottomPos + 2}% 100%, 0 100%)`,
                      transform: 'scale(1.1)',
                      transformOrigin: 'center'
                    }}
                  />
                  
                  {/* Imagem Wireframe - Lado direito da diagonal (sobrepõe levemente para eliminar gap) */}
                  <img
                    src="/assets/images/LadderchainWireframe.png"
                    alt="Wireframe da engenharia - Sistema de correntes para pneu"
                    className="absolute w-full h-full object-contain"
                    style={{
                      clipPath: `polygon(${topPos - 2}% 0, 100% 0, 100% 100%, ${bottomPos - 2}% 100%)`,
                      transform: 'scale(1.3)',
                      transformOrigin: 'center'
                    }}
                  />
                </div>
              </div>
            </>
          );
        })()}
      </div>
      {/* === Fim do Contêiner de Vídeo e Imagens === */}

        {/* Overlay escuro para melhor legibilidade */}
        <div className="absolute inset-0 bg-black opacity-50"></div>
        
        {/* Hero Content */}
        <main className="relative z-15 flex-1 flex flex-col items-left justify-center px-4">
          <div className="flex items-center gap-2 md:gap-4 pb-12">
            <div className="w-18 md:w-25 ml-5 lg:ml-36">
              <img src="/assets/images/ModlinkLogo.png" alt="Modlink Logo" />
            </div>
            <div className="w-100 md:w-150 pl-4 md:pl-8">
              <img src="/assets/logos/ModLinkWordmarkSVG.svg" alt="Modlink Logo" className="invert"/>
            </div>
          </div>
          
          <div className="font-displayed !text-white tracking-wider uppercase max-w-xl text-4xl sm:text-6xl ml-10 lg:ml-72">
            <span>Forget about your old chains. </span>
            <span className="font-orator text-orangicide text-3xl">
              And try our smarter ones.
            </span>
          </div>
        </main>
      </section>

      {/* 2 - modlink-about-section */}
      <section
        id="about" className="bg-[url(/assets/images/AboutBackground.png)] bg-center bg-cover"
        >
        <div className="container mx-auto px-4 py-12 pb-24">
          <div className="font-displayed !text-white tracking-wider uppercase text-5xl md:text-6xl mt-12">
            Designed by Tradesmen.
          </div>
          <div className="font-orator text-orangicide tracking-wider text-3xl md:text-4xl pb-12">
            Built for the Field
          </div>
          <Paragraph className="font-orator !text-white text-center text-base md:!text-2xl tracking-wider leading-relaxed max-w-6xl mx-auto pb-8">
            THE CHAIN INDUSTRY GOT COMFORTABLE. SELLING THE SAME OUTDATED GEAR FOR DECADES, ASSUMING NO ONE HAD THE TIME TO RETHINK IT. BUT WE WEREN'T BUYING IT. WE SAW HARDWORKING CREWS DEALING WITH SLOW INSTALLS, WEAK HARDWARE, AND TOOLS THAT CREATED MORE PROBLEMS THAN THEY SOLVED. SO WE DID WHAT TRADESMEN DO BEST: ROLLED UP OUR SLEEVES AND BUILT SOMETHING BETTER. WITH IN-HOUSE FABRICATION AND FIELD EXPERIENCE, WE ENGINEERED SOLUTIONS THAT SAVE TIME, REDUCE FRUSTRATION, AND THRIVE IN THE HARSHEST CONDITIONS.
          </Paragraph>
          <div className="flex justify-center md:justify-start">
            <button className="font-displayed text-2xl md:text-4xl w-75 md:w-95 h-12 md:h-16 tracking-wider btn-orangicide cursor-pointer" onClick={() => { navigate('/about'); }}>
              LEARN MORE
            </button>
          </div>
        </div>
      </section>

      {/* 3 - modlink-products-section */}
      <section id="products" className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat pb-24">
        <div className="container mx-auto px-4 py-12">
          <div className="font-displayed !font-normal text-white text-5xl md:text-6xl text-center">
            PRODUCTS
          </div>
        </div>

        <div className="relative">
          <Swiper
            centeredSlides={true} // <-- REATIVADO para o modo mobile
            loop={true}
            slidesPerGroup={1}
            navigation={{
              nextEl: '.swiper-button-next-custom',
              prevEl: '.swiper-button-prev-custom',
            }}
            modules={[Navigation]}
            className="product-swiper"
            breakpoints={{
              320: {
                slidesPerView: 1,
                spaceBetween: 20,
              },
              720: {
                slidesPerView: 2,
                spaceBetween: 50,
                centeredSlides: false,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 80,
                centeredSlides: false,
              },
              1280: {
                slidesPerView: 4,
                spaceBetween: 90,
                centeredSlides: false,
              },
            }}
          >
              {loopedProducts.map((product, index) => (
                <SwiperSlide key={`${product.id}-${index}`} style={{ width: '410px', height: 'auto' }}>
                  <div
                    onClick={() => { navigate('/merch'); window.scrollTo(0, 0); }}
                    className="h-full"
                  >
                    <ProductCard
                      imageUrl={product.imageUrl}
                      productName={product.name}
                      description={product.description}
                    />
                  </div>
                </SwiperSlide>
              ))}
          </Swiper>

          {/* Botões de Navegação Customizados */}
          <div className="swiper-button-prev-custom absolute top-1/3 -translate-y-1/2 left-1 lg:left-3 z-10 text-gray-300 cursor-pointer">
            <LeftOutlined className="text-[40px] sm:text-[60px] md:text-[80px] lg:text-[100px]" />
          </div>
          <div className="swiper-button-next-custom absolute top-1/3 -translate-y-1/2 right-1 lg:right-3 z-10 text-gray-300 cursor-pointer">
            <RightOutlined className="text-[40px] sm:text-[60px] md:text-[80px] lg:text-[100px]" />
          </div>
        </div>
      </section>
      
      {/* 4 - modlink-featured-product-section */}
      <section id="featproduct" className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat relative h-[55vh] sm:h-[60vh] md:h-[75vh] overflow-hidden flex items-center justify-center py-12">
        {/* Metade Inferior - Fundo Branco */}
        <div className="absolute bottom-0 left-0 right-0 h-[40%] bg-white z-0"></div>

        {/* Título "Featured Product" Fixo */}
        <div className="absolute top-0 text-center pointer-events-none z-10">
            <div className="font-displayed !font-normal text-white text-5xl tracking-wider md:text-6xl text-center">
                FEATURED PRODUCT
            </div>
        </div>

        {/* Imagem Centralizada */}
        <div className="absolute z-15 top-10 inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[23rem] h-[23rem] sm:w-[40rem] sm:h-[40rem] md:w-[45rem] md:h-[45rem] lg:w-[50rem] lg:h-[50rem] relative">
             <img 
               src="/assets/images/RevoHookCool.png" 
               alt="Revo Hook" 
               className="w-full h-full object-contain drop-shadow-2xl transform hover:scale-105 transition-transform duration-500"
             />
          </div>
        </div>
      </section>

      {/* 5 - modlink-why-choose-section */}
      <section id="mission" className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat w-full pt-16 pb-24">
        <div className="container mx-auto px-4 text-center">
          <div className="font-displayed !font-normal text-white text-5xl md:text-6xl mb-6">
            OUR MISSION & VALUES
          </div>
          <Paragraph className="font-orator text-orangicide !text-xl text-center max-w-xl mx-auto !mb-24">
            At Mod-Link, we are driven by a commitment to excellence and innovation in everything we do
          </Paragraph>
                    
          {/* Mission and Values Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-18 gap-y-16 md:gap-y-16 max-w-8xl mx-auto items-stretch">
            
            <div className="relative flex flex-col items-center h-full">
              <div className="icon-badge-orangicide absolute -top-10 left-1/2 -translate-x-1/2 z-10">
                <HiOutlineLightBulb className="text-4xl" />
              </div>
              <div 
                className="relative bg-transparent border-4 border-white/50 text-white px-6 py-8 pt-10 flex flex-col items-center justify-between transition-all duration-300 hover:border-white hover:shadow-lg hover:shadow-white/20 group w-full h-full"
                style={{ clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)' }}
              >
                <div
                  className="pointer-events-none absolute top-0 left-0 bg-white/50 transition-all duration-300 group-hover:bg-white"
                  style={{ width: 20, height: 20, clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                />
                <div
                  className="pointer-events-none absolute bottom-0 right-0 bg-white/50 transition-all duration-300 group-hover:bg-white"
                  style={{ width: 20, height: 20, clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                />
                <Title level={1} className="font-displayed !font-normal text-white tracking-wider text-center !mb-4">INNOVATION</Title>
                <Paragraph className="font-orator !text-xl text-orangicide text-center !mb-0 flex-1 flex items-center justify-center">
                  We continuously push the boundaries of what's possible in traction technology, developing new solutions that outperform traditional methods.
                </Paragraph>
              </div>
            </div>
            
            <div className="relative flex flex-col items-center h-full">
              <div className="icon-badge-orangicide absolute -top-10 left-1/2 -translate-x-1/2 z-10">
                <PiLeaf className="text-4xl" />
              </div>
              <div 
                className="relative bg-transparent border-4 border-white/50 text-white px-6 py-8 pt-10 flex flex-col items-center justify-between transition-all duration-300 hover:border-white hover:shadow-lg hover:shadow-white/20 group w-full h-full"
                style={{ clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)' }}
              >
                <div
                  className="pointer-events-none absolute top-0 left-0 bg-white/50 transition-all duration-300 group-hover:bg-white"
                  style={{ width: 20, height: 20, clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                />
                <div
                  className="pointer-events-none absolute bottom-0 right-0 bg-white/50 transition-all duration-300 group-hover:bg-white"
                  style={{ width: 20, height: 20, clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                />
                <Title level={1} className="font-displayed !font-normal text-white tracking-wider text-center !mb-4">QUALITY</Title>
                <Paragraph className="font-orator !text-xl text-orangicide text-center !mb-0 flex-1 flex items-center justify-center">
                  We never compromise on materials or craftsmanship. Our products are built to withstand extreme conditions and provide reliable performance when it matters most.
                </Paragraph>
              </div>
            </div>
            
            <div className="relative flex flex-col items-center h-full">
              <div className="icon-badge-orangicide absolute -top-10 left-1/2 -translate-x-1/2 z-10">
                <FiUsers className="text-4xl" />
              </div>
              <div 
                className="relative bg-transparent border-4 border-white/50 text-white px-6 py-8 pt-10 flex flex-col items-center justify-between transition-all duration-300 hover:border-white hover:shadow-lg hover:shadow-white/20 group w-full h-full"
                style={{ clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)' }}
              >
                <div
                  className="pointer-events-none absolute top-0 left-0 bg-white/50 transition-all duration-300 group-hover:bg-white"
                  style={{ width: 20, height: 20, clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                />
                <div
                  className="pointer-events-none absolute bottom-0 right-0 bg-white/50 transition-all duration-300 group-hover:bg-white"
                  style={{ width: 20, height: 20, clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                />
                <Title level={1} className="font-displayed !font-normal text-white text-center !mb-4">CUSTOMER FOCUS</Title>
                <Paragraph className="font-orator !text-xl text-orangicide text-center !mb-0 flex-1 flex items-center justify-center">
                  We work closely with our customers to understand their specific needs and challenges, developing customized solutions that help them operate more efficiently and safely.
                </Paragraph>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6 - modlink-testimonials-section */}
      {testimonials.length > 0 && (
        <section id="testimonials" className="bg-[url(/assets/images/Background.png)] bg-bottom bg-cover bg-no-repeat pb-12">
          <div className="container mx-auto px-4 py-12">
            <div className="font-displayed !font-normal text-white text-4xl md:text-6xl text-center mb-12">
              WHAT OUR CUSTOMERS SAY
            </div>
            
            {/* Testimonials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {testimonials.map((testimonial) => {
                // Helper function to get avatar URL
                const getAvatarUrl = () => {
                  if (testimonial.avatar) {
                    if (testimonial.avatar.startsWith('http')) {
                      return testimonial.avatar;
                    }
                    const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'modlink-production';
                    const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
                    return `https://${bucketName}.s3.${region}.amazonaws.com/${testimonial.avatar}`;
                  }
                  return undefined;
                };

                return (
                  <div key={testimonial.id} className="relative">
                    <div 
                      className="relative border-4 border-white flex flex-col items-center p-8 text-center hover:border-orangicide transition-all duration-300"
                      style={{
                        clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)'
                      }}
                    >
                      <div
                        className="pointer-events-none absolute top-0 left-0"
                        style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                      />
                      <div
                        className="pointer-events-none absolute bottom-0 right-0"
                        style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                      />
                      <div className="flex flex-col items-center text-center gap-4">
                        {/* Avatar */}
                        {getAvatarUrl() ? (
                          <Avatar
                            size={80}
                            src={getAvatarUrl()}
                            alt={testimonial.name}
                            className="border-2 border-zinc-600"
                          />
                        ) : (
                          <Avatar
                            size={80}
                            icon={<UserOutlined />}
                            className="bg-zinc-700 text-white border-2 border-zinc-600"
                          />
                        )}

                        {/* Description (Job Title/Company) */}
                        {testimonial.description && (
                          <div className="font-orator text-orangicide text-sm uppercase tracking-wider font-semibold">
                            {testimonial.description}
                          </div>
                        )}

                        {/* Rating */}
                        <Rate
                          disabled
                          value={testimonial.stars}
                          className="text-orangicide"
                        />

                        {/* Testimonial Text */}
                        <div className="font-orator text-white text-base leading-relaxed font-medium">
                          "{testimonial.text}"
                        </div>

                        {/* Name */}
                        <div className="font-displayed !font-normal text-white text-xl mt-2">
                          {testimonial.name}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 7 - Need Help Section (Contact Us) */}
      <section id="contact-us" className="bg-charcoal text-white py-16 md:py-24">
        <div className="container mx-auto px-4 text-center">
          <div className="font-displayed !font-normal text-white text-4xl md:text-6xl uppercase pb-8">
            NEED HELP FINDING THE RIGHT PRODUCT?
          </div>
          <div className="font-orator font-normal text-white text-xl md:text-3xl max-w-6xl mx-auto mt-4">
            Our team of experts is ready to assist you in selecting the perfect traction solution for your specific needs.
          </div>
          <div className="text-center pt-16">
            <button className="font-displayed text-2xl md:text-3xl w-75 md:w-95 h-12 md:h-16 tracking-wider btn-orangicide cursor-pointer" onClick={() => setIsContactModalVisible(true)}>
              CONTACT OUR EXPERTS
            </button>
          </div>
          
          <div className="container mx-auto px-4 mt-22">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <div className="relative">
                <div 
                  className="relative bg-[url(/assets/images/Background.png)] bg-left bg-no-repeat border-4 border-white flex flex-col items-center p-8 text-center"
                  style={{
                    clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)',
                    backgroundSize: '220%'
                  }}
                >
                  <div
                    className="pointer-events-none absolute top-0 left-0"
                    style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                  />
                  <div
                    className="pointer-events-none absolute bottom-0 right-0"
                    style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                  />
                  <PhoneOutlined className="text-orangicide text-4xl mb-4" />
                  <Title level={3} className="font-orator !font-normal text-white">CALL US</Title>
                  <Paragraph className="font-orator !text-gray-400 !text-lg !mb-1">866.308.5465</Paragraph>
                  <Paragraph className="font-orator !text-gray-400 !text-base">Monday - Friday: 7am - 5pm</Paragraph>
                </div>
              </div>
              <div className="relative">
                <div 
                  className="relative bg-[url(/assets/images/Background.png)] bg-center g-no-repeat border-4 border-white flex flex-col items-center p-8 text-center"
                  style={{
                    clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)',
                    backgroundSize: '220%'
                  }}
                >
                  <div
                    className="pointer-events-none absolute top-0 left-0"
                    style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                  />
                  <div
                    className="pointer-events-none absolute bottom-0 right-0"
                    style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                  />
                  <MailOutlined className="text-orangicide text-4xl mb-4" />
                  <Title level={3} className="font-orator !font-normal text-white">EMAIL US</Title>
                  <Paragraph className="font-orator !text-gray-400 !text-lg !mb-1">info@modlink.com</Paragraph>
                  <Paragraph className="font-orator !text-gray-400 !text-base">We respond within 24h</Paragraph>
                </div>
              </div>
              <div className="relative">
                <div 
                  className="relative bg-[url(/assets/images/Background.png)] bg-right bg-no-repeat border-4 border-white flex flex-col items-center p-8 text-center"
                  style={{
                    clipPath: 'polygon(-20px 0, 100% 0, 100% 100%, 0 100%, 0 -20px)',
                    backgroundSize: '220%'
                  }}
                >
                  <div
                    className="pointer-events-none absolute top-0 left-0"
                    style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(0 0, 100% 0, 0 100%)' }}
                  />
                  <div
                    className="pointer-events-none absolute bottom-0 right-0"
                    style={{ width: 20, height: 20, backgroundColor: 'white', clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                  />
                  <EnvironmentOutlined className="text-orangicide text-4xl mb-4" />
                  <Title level={3} className="font-orator !font-normal text-white">MAILING ADDRESS</Title>
                  <Paragraph className="font-orator !text-gray-400 !text-lg">PO Box 1921<br />Truckee, CA 96160</Paragraph>
                </div>
              </div>
            </div>
          </div>          
        </div>
      </section>

      {/* 8 - Our Family of Brands Section */}
      <section className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat pb-12">
        <div className="container mx-auto">
          <div className="font-displayed !font-normal text-white text-center text-4xl md:text-5xl uppercase py-12">
            OUR FAMILY OF BRANDS
          </div>
          
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 pb-6">
            {/* Logos das marcas */}
            <a 
              href="https://ruppertinc.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/RuppertLogo.svg" 
                alt="Ruppert Logo" 
                className="max-w-full max-h-full" 
              />
            </a>
            <a 
              href="https://estabrookinc.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/EstabrookLogo.svg" 
                alt="Estabrook Logo" 
                className="max-w-full max-h-full" 
              />
            </a>
            <a 
              href="https://modlink.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/ModLinkLogo.svg" 
                alt="Modlink Logo" 
                className="max-w-full max-h-full"
              />
            </a>
            <div className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center">
              <img 
                src="/assets/logos/SnowSyncLogo.svg" 
                alt="Snow Sync Logo" 
                className="max-w-full max-h-full" 
              />
            </div>
            <div className="w-[100px] md:w-[150px] h-[70px] md:mt-2 flex items-center justify-center">
              <img 
                src="/assets/logos/asrLogo.svg" 
                alt="ASR Logo" 
                className="max-w-full max-h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Experts Modal */}
      <ContactExpertsModal
        visible={isContactModalVisible}
        onClose={() => setIsContactModalVisible(false)}
        source="landing"
      />
    </>
  );
};